const field = (key, label, context) => ({ key, label, context });
const item = (label, description) => ({ label, description });
const section = (title, items) => ({ title, items });

export default {
    'activity.activity-details': {
        title: 'Activity details',
        intro: 'These settings define the activity itself and how dates and volunteer assignments are handled.',
        fields: [
            field('activity_detail_status', 'Status', 'Inactive activities are not used for automatic scheduling.'),
            field('activity_detail_priority', 'Priority', 'Priority helps determine ordering when activities are processed.'),
            field('activity_detail_starttime', 'Start Time', 'The local start time used when a recurrence creates an instance.'),
            field('activity_detail_duration', 'Duration', 'Sets the length of each generated instance.'),
            field('activity_detail_recurrenceoffset', 'Recurrence Offset', 'Controls how far ahead the system looks when generating instances.'),
            field('activity_detail_autosched', 'Auto Schedule', 'When enabled, the scheduler can assign eligible volunteers to open workgroup positions.'),
            field('activity_detail_autogen', 'Auto Generate', 'When enabled, matching recurrence dates can create activity instances.'),
            field('activity_detail_autosub', 'Auto Substitute', 'Allows the scheduler to seek a replacement after an assignment is declined.'),
            field('activity_detail_pubview', 'Publicly Viewable', 'Controls whether this activity can appear in public schedule views.'),
            field('activity_detail_autocomplete', 'Auto-Scheduler Completion Notification', 'Controls the completion alert for activity scheduling.'),
            field('activity_detail_send_admin_report', 'Send Admin Report', 'Controls whether activity administrators receive reports.'),
            field('activity_detail_allschedulesurl', 'All Schedules URL', 'A read-only link that can be embedded on the location website.'),
        ],
        sections: [section('Other fields', [
            item('ID and Location ID', 'Read-only identifiers for this activity and its location.'),
            item('Name and Description', 'Identify the activity in schedules and explain its purpose to administrators and volunteers.'),
            item('Last Instance Date', 'The most recent generated date used as a reference when creating the next recurrence instance.'),
            item('Reset', 'Sets the last instance date back to the start of the primary recurrence.'),
            item('Notes', 'Internal context saved with the activity.'),
            item('View Public Activities/Events', 'Opens the public schedule view for this location.'),
        ])],
    },
    'activity.workgroups': {
        title: 'Activity workgroups',
        intro: 'A workgroup is a role or team that needs volunteers for each activity instance.',
        sections: [section('Values in the workgroup list', [
            item('Workgroup Name and Description', 'Identify the role and what volunteers in it do.'),
            item('Status and Setup Status', 'Show whether the workgroup is active and whether its required settings are complete.'),
            item('Person Count and Skill', 'Show how many people are needed and the skill volunteers must have.'),
            item('Priority', 'Helps order workgroups during automatic scheduling.'),
            item('SMS Reminders', 'Show whether primary or secondary text reminders are configured.'),
            item('Auto-Schedule and Auto-Substitute', 'Show whether volunteers can be assigned automatically and whether a replacement can be sought after a decline.'),
            item('Swap Enabled and Allow Overlap', 'Control whether assignments can be swapped and whether a volunteer may have overlapping assignments.'),
            item('Sign-Up Only and Convert-To-Request', 'Show whether volunteers choose open positions themselves and how unfilled positions are presented.'),
            item('Auto-Decline', 'Shows whether unanswered assignments can be declined automatically.'),
            item('Publicly Viewable and Teams Enabled', 'Control public visibility and whether the workgroup rotates through configured teams.'),
            item('Summary', 'A compact overview of the workgroup configuration.'),
        ])],
    },
    'activity.recurrences': {
        title: 'Activity recurrences',
        intro: 'Recurrences describe the repeating date pattern from which activity instances can be generated.',
        sections: [section('Values in the recurrence list', [
            item('ID', 'The recurrence record identifier.'),
            item('Recurrence Name', 'A short label used to distinguish this date pattern.'),
            item('Description', 'Notes about the purpose or timing of the pattern.'),
            item('Recurrence', 'A readable summary of the repeat rule and its date limits.'),
            item('Add, Edit, and Delete', 'Create or maintain the date rules attached to this activity.'),
        ])],
    },
    'activity.instances': {
        title: 'Activity instances',
        intro: 'An instance is one dated occurrence of an activity. Instances can be generated from recurrences or managed as specific dates.',
        sections: [section('Values in the instance list', [
            item('ID', 'The unique identifier for the dated occurrence.'),
            item('Start Date and End Date', 'The scheduled date and time window for this occurrence.'),
            item('Status', 'Shows whether the instance is active.'),
            item('Completed Auto-Schedule', 'Indicates whether automatic assignment has completed for this instance.'),
            item('Summary', 'A compact view of the instance and its workgroup staffing.'),
            item('Last Auto-Schedule Date and Last Admin Report Date', 'Show when those background processes last ran for the instance.'),
            item('Note', 'Context recorded for this specific occurrence.'),
            item('Logs', 'Show recorded system activity for the selected instance.'),
        ])],
    },
    'activity.preferred-users': {
        title: 'Preferred users',
        intro: 'This list shows volunteers who have marked this activity as one they prefer.',
        sections: [section('Values in the list', [
            item('All Users', 'People available to be added as preferred users for this activity.'),
            item('Current Users who prefer this activity', 'People who currently have this activity in their preferences.'),
            item('Name and Email', 'Identify each person and provide their contact address.'),
            item('Save', 'Apply changes to which users prefer this activity. A preference does not guarantee an assignment.'),
        ])],
    },
    'activity.activity-admins': {
        title: 'Activity administrators',
        intro: 'Activity administrators can manage this activity and receive activity-level notifications.',
        sections: [section('Values in the list', [
            item('All Users', 'People available to be selected as activity administrators.'),
            item('Current Activity Admins', 'People who currently have activity-level administrative access.'),
            item('Name and Email', 'Identify each person and their contact address.'),
            item('Save', 'Apply changes to the activity administrator list.'),
        ])],
    },
    'workgroup.workgroup-details': {
        title: 'Workgroup details',
        intro: 'These settings define staffing, visibility, notifications, and automatic scheduling for this workgroup.',
        fields: [
            field('workgroup_detail_status', 'Status', 'Inactive workgroups are excluded from scheduling.'),
            field('workgroup_detail_skill', 'Skill', 'Volunteers generally need this skill to be considered for the workgroup.'),
            field('workgroup_detail_personsneeded', 'Person Count', 'The number of volunteers needed for each instance.'),
            field('workgroup_detail_wgsms', 'Send SMS', 'Controls the primary workgroup text reminder.'),
            field('workgroup_detail_2ndwgsms', 'Send Secondary SMS', 'Controls an additional workgroup text reminder.'),
            field('workgroup_detail_2ndwgsmsdays', 'Secondary SMS Days', 'Sets how many days before the instance the secondary reminder is sent.'),
            field('workgroup_detail_remindertype', 'SMS Reminder Type', 'Chooses which reminder message is used.'),
            field('workgroup_detail_signuponly', 'Sign Up Only', 'Makes the position volunteer sign-up rather than automatic assignment.'),
            field('workgroup_detail_signuponly_allow_multiple', 'Allow Multiple Assignments', 'Controls whether a volunteer may sign up for more than one position.'),
            field('workgroup_detail_advertisesignup', 'Advertise Sign Up', 'Controls whether open sign-up opportunities are promoted.'),
            field('workgroup_detail_publicview', 'Publicly Viewable', 'Controls visibility in public schedule views.'),
            field('workgroup_detail_autosched', 'Auto Schedule', 'Allows the scheduler to assign eligible volunteers automatically.'),
            field('workgroup_detail_autosub', 'Auto Substitute', 'Allows automatic replacement attempts after a decline.'),
            field('workgroup_detail_converttosignup', 'Convert to Sign-up', 'Allows an unfilled assignment to become an open sign-up opportunity.'),
            field('workgroup_detail_enableswap', 'Enable Swap', 'Allows volunteers to request an assignment swap.'),
            field('workgroup_detail_allowoverlap', 'Allow Overlap', 'Controls whether volunteers with overlapping assignments may be considered.'),
            field('workgroup_detail_autocomplete', 'Completion Notification', 'Controls the notification sent when automatic scheduling finishes.'),
            field('workgroup_detail_autosubstitute_adminalert', 'Auto-Substitute/Swap Notification', 'Controls administrator alerts about replacement or swap activity.'),
            field('workgroup_detail_send_admin_report', 'Send Admin Report', 'Controls workgroup reports sent to administrators.'),
            field('workgroup_detail_ignore_preferences', 'Ignore Preferences', 'Controls whether volunteer activity and workgroup preferences are considered.'),
            field('workgroup_detail_priority', 'Priority', 'Helps determine scheduling order; higher values have higher priority.'),
            field('workgroup_detail_declinedays', 'Decline Days', 'Sets the decline deadline relative to the instance date.'),
            field('workgroup_detail_autodecline', 'Auto Decline', 'Controls automatic decline of assignments that remain unanswered.'),
            field('workgroup_detail_teams', 'Schedule by Teams', 'Enables sequential scheduling from the teams configured on the Teams tab.'),
            field('workgroup_detail_nextteam', 'Next Team', 'Selects which team is scheduled next.'),
            field('workgroup_detail_teamrotatemethod', 'Rotate Teams by', 'Chooses how the next team is determined.'),
            field('workgroup_detail_skipteam', 'Skip Team if Unavailable', 'Allows the scheduler to move on when a team cannot fill the needed positions.'),
            field('workgroup_detail_adminemail', 'Admin Reply-to Email', 'Sets the reply-to address used for workgroup messages.'),
            field('workgroup_detail_icalurl', 'iCal URL', 'A read-only calendar subscription link for the workgroup.'),
        ],
        sections: [section('Other fields', [
            item('ID, Workgroup Name, and Description', 'Identify the workgroup and explain its role.'),
            item('Activity', 'The activity this workgroup belongs to.'),
            item('Auto-decline settings', 'The timing values determine when unanswered assignments are declined.'),
        ])],
    },
    'workgroup.schedule-by-teams': {
        title: 'Schedule by teams',
        intro: 'Teams let the scheduler rotate through smaller volunteer groups instead of considering everyone at once.',
        sections: [section('Values in the team list', [
            item('Team Name', 'Identifies a team of volunteers.'),
            item('Sequence', 'The order in which teams are considered.'),
            item('Count', 'The number of people assigned to the team.'),
            item('Team Members', 'The volunteers included in the team.'),
            item('Last Schedule Date', 'Shows when the team was most recently used.'),
            item('Team rotation settings', 'The Details tab chooses the next team and how the sequence advances.'),
        ])],
    },
    'workgroup.skilled-volunteers': {
        title: 'Preferred volunteers',
        intro: 'This list shows volunteers with the required skill who have expressed a preference for this workgroup.',
        sections: [section('Values in the list', [
            item('Preferred volunteers', 'People who prefer this workgroup and have its required skill.'),
            item('Name and Email', 'Identify the volunteer and their contact address.'),
            item('Skill', 'The required skill determines which volunteers appear in this list.'),
        ])],
    },
    'workgroup.instance-workgroups': {
        title: 'Workgroup instances',
        intro: 'Each row is a dated instance workgroup created from this workgroup.',
        sections: [section('Values in the list', [
            item('Instance Date', 'When the workgroup needs volunteers.'),
            item('Instance Workgroup Name and Description', 'Identify the role for that specific occurrence.'),
            item('Person Count and Persons Scheduled', 'Compare the number needed with the number currently assigned.'),
            item('Skill', 'The skill associated with the position.'),
            item('Team', 'The team selected for this occurrence, when team scheduling is enabled.'),
            item('Logs', 'Show recorded system activity for an instance workgroup.'),
        ])],
    },
    'workgroup.workgroup-admins': {
        title: 'Workgroup administrators',
        intro: 'Workgroup administrators can manage this workgroup and may receive its reports and alerts.',
        sections: [section('Values in the list', [
            item('All Volunteers', 'People available to be selected as workgroup administrators.'),
            item('Administrators for this workgroup', 'People who currently administer this workgroup.'),
            item('Name and Email', 'Identify each person and their contact address.'),
            item('Save', 'Apply changes to the workgroup administrator list.'),
        ])],
    },
    'workgroup.workgroup-preferences': {
        title: 'Workgroup rules',
        intro: 'Rules affect how eligible volunteers are evaluated and ordered for this workgroup.',
        sections: [section('Values in the rule list', [
            item('Rule Number', 'The order in which the rule is listed.'),
            item('Title', 'The rule’s name.'),
            item('Description', 'A summary of what the rule evaluates.'),
            item('Rule settings', 'Editing a rule changes its criteria or effect; rules are used alongside availability and other scheduling checks.'),
        ])],
    },
    'skill.skill-details': {
        title: 'Skill details',
        intro: 'A skill describes a qualification used to identify volunteers for workgroups.',
        fields: [
            field('skill_detail_defaultskill', 'Default Skill', 'Indicates whether this is a built-in default skill.'),
            field('skill_detail_autoadd', 'Auto Add Person', 'Controls whether interested users can be added automatically.'),
            field('skill_detail_publicview', 'Publicly Viewable', 'Controls whether the skill is visible in public views.'),
            field('skill_detail_preselect', 'Pre-select during registration', 'Controls whether this skill is selected by default during registration.'),
        ],
        sections: [section('Other fields', [
            item('ID', 'The unique identifier for the skill.'),
            item('Name and Description', 'Identify the qualification and explain what it represents.'),
        ])],
    },
    'skill.add-remove': {
        title: 'People with this skill',
        intro: 'This list shows the people currently assigned this skill at the location.',
        sections: [section('Values in the list', [
            item('Person', 'The volunteer or user assigned to the skill.'),
            item('Email', 'The person’s contact address.'),
            item('Selection controls', 'Add people to or remove people from this skill.'),
        ])],
    },
    'skill.update': {
        title: 'Skill levels',
        intro: 'Skill levels record each person’s proficiency for this skill.',
        sections: [section('Values in the list', [
            item('First Name and Last Name', 'Identify the person whose level is shown.'),
            item('Email', 'The person’s contact address.'),
            item('Skill Level', 'The proficiency value used as information when reviewing skilled volunteers.'),
        ])],
    },
    'skill.workgroups': {
        title: 'Workgroups using this skill',
        intro: 'This list shows where the skill is required.',
        sections: [section('Values in the list', [
            item('Activity/Event', 'The activity that contains the workgroup.'),
            item('Workgroup Name and Description', 'Identify the role that requires this skill.'),
            item('Persons Needed', 'The number of volunteers requested for the workgroup.'),
            item('Status', 'Shows whether the workgroup is active.'),
        ])],
    },
    'skill.interested-not-assigned': {
        title: 'Interested users',
        intro: 'These people have expressed interest in the skill but are not currently assigned it.',
        sections: [section('Values in the list', [
            item('Person ID', 'The unique identifier for the person.'),
            item('First Name and Last Name', 'Identify the interested person.'),
            item('Email', 'The person’s contact address.'),
        ])],
    },
    'instance.instance-details': {
        title: 'Instance details',
        intro: 'An instance is one specific dated occurrence of an activity.',
        fields: [
            field('activityinstance_detail_schedcomplete', 'Completed Auto Schedule', 'Shows whether automatic scheduling has completed for this occurrence.'),
            field('activityinstance_detail_lastscheddate', 'Last Schedule Date', 'Shows when automatic scheduling last ran for this occurrence.'),
            field('activityinstance_detail_lastreportdate', 'Last Admin Report Date', 'Shows when the last administrator report was sent.'),
        ],
        sections: [section('Other fields', [
            item('ID', 'The unique identifier for this occurrence.'),
            item('Name and Description', 'Inherited labels describing the source activity.'),
            item('Start Date/Time and End Date/Time', 'Define when this occurrence begins and ends.'),
            item('Status', 'Controls whether this occurrence is active.'),
            item('Notes', 'Context saved for this specific occurrence.'),
        ])],
    },
    'instance.instance-workgroups': {
        title: 'Instance workgroups',
        intro: 'These are the roles that need staffing for this specific activity date.',
        sections: [section('Values in the list', [
            item('Workgroup Name and Description', 'Identify the role for this instance.'),
            item('Summary', 'A compact overview of staffing and assignment status.'),
            item('Person Count and Persons Scheduled', 'Compare the number needed with current assignments.'),
            item('Priority', 'Indicates the workgroup’s scheduling priority.'),
            item('Skill', 'Shows the qualification associated with the role.'),
            item('Team', 'Shows the team selected for this instance, when applicable.'),
            item('Logs', 'Show recorded system activity for the selected workgroup.'),
        ])],
    },
    'instance.instance-admins': {
        title: 'Instance administrators',
        intro: 'Instance administrators can manage this specific dated occurrence.',
        sections: [section('Values in the list', [
            item('All Users', 'People available to be selected as instance administrators.'),
            item('Current Admins', 'People who currently administer this occurrence.'),
            item('Name and Email', 'Identify each person and their contact address.'),
            item('Save', 'Apply changes to the instance administrator list.'),
        ])],
    },
    'instance-workgroup.workgroup-details': {
        title: 'Instance workgroup details',
        intro: 'This record represents one workgroup’s staffing for one activity instance.',
        fields: [
            field('activityinstanceworkgroup_detail_personcount', 'Person Count', 'The number of people requested for this instance workgroup.'),
            field('activityinstanceworkgroup_detail_skill', 'Skill', 'The qualification associated with this instance workgroup.'),
            field('activityinstanceworkgroup_detail_workgroup', 'Workgroup', 'The activity workgroup this dated record is based on.'),
            field('activityinstanceworkgroup_detail_signuponly', 'Sign Up Only', 'Indicates that volunteers sign themselves up rather than being assigned automatically.'),
            field('activityinstanceworkgroup_detail_team', 'Team', 'The team assigned to this instance workgroup.'),
            field('activityinstanceworkgroup_detail_schedoptions', 'Scheduling Options', 'Summarizes automatic scheduling, substitution, swaps, overlap, reminders, and related behavior.'),
            field('activityinstanceworkgroup_detail_adminreportdate', 'Last Admin Report Date', 'Shows when the last administrator report was sent.'),
            field('activityinstanceworkgroup_detail_userreportdate', 'Last Who-Is-On Report Date', 'Shows when the last report of assigned people was sent.'),
        ],
        sections: [section('Other fields', [
            item('ID, Name, and Description', 'Identify this dated workgroup and its role.'),
            item('Status', 'Shows whether this instance workgroup is active.'),
            item('View Persons', 'Opens the people associated with the selected skill.'),
        ])],
    },
    'instance-workgroup.add-remove': {
        title: 'Scheduled persons',
        intro: 'This tab manages the people assigned to this instance workgroup.',
        sections: [section('Values in the lists', [
            item('All Users with skill', 'People who have the skill required by this workgroup and can be considered for assignment.'),
            item('Scheduled volunteers', 'People currently assigned to this workgroup and instance date.'),
            item('Name and contact detail', 'Identify each person; the supporting text shows their email or other available contact detail.'),
            item('Save', 'Apply changes to the scheduled volunteer list.'),
        ])],
    },
    'instance-workgroup.manage-admins': {
        title: 'Instance workgroup administrators',
        intro: 'These administrators can help manage this workgroup for the selected activity date.',
        sections: [section('Values in the list', [
            item('All Users', 'People available to be selected as instance-workgroup administrators.'),
            item('Current Admins', 'People who currently administer this instance workgroup.'),
            item('Name and Email', 'Identify each person and their contact address.'),
            item('Save', 'Apply changes to the administrator list.'),
        ])],
    },
    organization: {
        title: 'Organization',
        intro: 'Organization details identify the organization, its contact information, status, locations, and administrators.',
        sections: [
            section('Organization fields', [
                item('ID', 'The unique identifier for the organization.'),
                item('Name and Description', 'Identify the organization and describe it.'),
                item('Email and Phone Number', 'Contact details associated with the organization.'),
                item('Status', 'An inactive organization is not processed by the auto-engine.'),
                item('Current Balance', 'The organization’s available billing balance.'),
            ]),
            section('Locations list', [
                item('ID and Name', 'Identify each location belonging to the organization.'),
                item('Balance and Status', 'Show each location’s billing balance and whether it is active.'),
                item('Description, Phone, Email, Country, and Timezone', 'Summarize each location’s profile and local-time settings.'),
                item('Created Date, Engine Start, and Engine End', 'Show when a location was created and the recorded start/end of auto-engine activity.'),
                item('Actions', 'Edit a location or remove it.'),
            ]),
            section('Organization administrators', [
                item('All Users', 'People available to administer the organization.'),
                item('Current Admins', 'People who currently have organization administrator access.'),
            ]),
            section('Add New Location', [
                item('Name and Description', 'Identify the new location and describe it.'),
                item('Email and Phone Number', 'Set the location’s contact details.'),
                item('Country and Timezone', 'Set regional settings used for local presentation and scheduling.'),
            ]),
        ],
    },
    location: {
        title: 'Location',
        intro: 'Location settings control its contact details, public access, reminders, scheduling defaults, and feature limits.',
        fields: [
            field('location_detail_email', 'Email', 'The location’s contact address.'),
            field('location_detail_priority', 'Location Priority', 'Helps order location processing.'),
            field('location_detail_status', 'Status', 'Inactive locations are skipped by automatic schedule processing.'),
            field('location_detail_showads', 'Display Bible-based Content', 'Controls whether Bible-based content appears in supported views.'),
            field('location_detail_showinspirations', 'Display Inspirational Messages', 'Controls inspirational messages in supported views.'),
            field('location_detail_selfregister', 'User Self Register', 'Controls whether users may register themselves.'),
            field('location_detail_searchable', 'Show location in Join Organization search', 'This controls search discoverability only; it does not change existing memberships or the Self Register approval setting.'),
            field('location_detail_viewsched', 'Users Can View All Schedules', 'Controls schedule visibility for users.'),
            field('location_detail_prefactwg', 'Choose Activities/Workgroups', 'Controls whether non-admin users can select activity and workgroup preferences.'),
            field('location_detail_enablereminders', 'Enable Reminders', 'Controls SMS and email reminders for this location.'),
            field('location_detail_autocomplete', 'Auto-Scheduler Notification', 'Controls notification when automatic scheduling finishes.'),
            field('location_detail_send_admin_report', 'Send Admin Report', 'Controls the periodic volunteer schedule report sent to Location admins.'),
            field('location_detail_send_admin_report_day', 'Send Admin Report Day', 'The weekday on which the periodic volunteer schedule report is sent; Thursday is used when no day is set.'),
            field('location_detail_defaultskill', 'Default Skill', 'The skill selected by default in relevant workflows.'),
            field('location_detail_defaultviewmode', 'Default View Mode', 'Chooses the initial schedule presentation.'),
            field('location_detail_sendsmsdays', 'SMS Days Before Instance', 'Sets how far ahead location-level text reminders are sent.'),
            field('location_detail_excludeemaildays', 'Exclude Email Reminder Days', 'Days on which volunteer email reminders are not sent.'),
            field('location_detail_locurl', 'Location Website URL', 'The public website address for the location.'),
            field('location_detail_customurl', 'Custom URL', 'A custom location URL used by supported links.'),
            field('location_detail_customloginurl', 'Custom Login URL', 'The configured login destination.'),
            field('location_detail_allschedulesurl', 'All Schedules URL', 'A read-only link that can be embedded on the location website.'),
            field('location_detail_reportdate', 'Last Admin Report Date', 'Shows when the last location administrator report was sent.'),
        ],
        sections: [
            section('Other location settings', [
                item('ID, Name, and Description', 'Identify the location and describe its purpose.'),
                item('Phone Number, Country, and Time Zone', 'Set contact and regional settings used by local schedules.'),
                item('Enable AI Chat and AI Schedule Generation', 'Control whether the corresponding AI features are available at this location.'),
                item('Custom Login URL', 'Read-only value showing the resolved login address.'),
                item('All Schedules URL', 'Copy the public schedule link to share or embed.'),
            ]),
            section('Monthly Cost Limits', [
                item('AI Chat Monthly Limit', 'Caps monthly spending on AI chat; zero is shown as unlimited.'),
                item('AI Schedule Generation Monthly Limit', 'Caps monthly spending on AI-powered scheduling; zero is shown as unlimited.'),
                item('SMS Monthly Limit', 'Caps monthly SMS spending for the location.'),
                item('Monthly Spend Limit', 'Caps total monthly feature spending for the location.'),
            ]),
            section('Additional pages', [
                item('Page cards', 'Open related location tools and configuration pages.'),
                item('What’s Next?', 'Shows suggested next steps for completing location setup.'),
            ]),
        ],
    },
    'user.user-details': {
        title: 'User details',
        intro: 'Review and update the user’s profile, account, contact, and notification details. Some fields are read-only or limited by your permissions.',
        fields: [
            field('person_detail_proxyemail', 'Use Proxy Email and Proxy Email', 'Choose whether messages use a proxy address instead of the user’s own email address.'),
            field('person_detail_confirmed', 'Confirmed', 'Shows whether the user’s account has been confirmed; changes are restricted to super admins.'),
            field('person_detail_regtype', 'Registration Type', 'Identifies the user’s registration type; changes are restricted to super admins.'),
            field('person_detail_smsalerts', 'Enable SMS/Text alerts', 'Controls whether the user can receive text alerts.'),
            field('person_detail_lastemaildate', 'Last Email Date', 'Read-only date of the last email sent to the user.'),
            field('person_detail_lastsignupdate', 'Last Sign-up Email Date', 'Read-only date of the last sign-up email sent to the user.'),
            field('person_detail_lastlogondate', 'Last Logon Date', 'Read-only date of the user’s last login.'),
            field('person_detail_icalurl', 'iCal URL', 'Read-only calendar subscription link for the user.'),
        ],
        sections: [section('Other fields and actions', [
            item('ID', 'Read-only identifier for the user.'),
            item('First Name, Middle Name, Last Name, and Email', 'Identify the user and provide their account email address.'),
            item('Password', 'Enter a new password when you have permission to update it; leaving it blank does not set a new password.'),
            item('Country, TimeZone, Mobile, and Gender', 'Profile and regional contact details.'),
            item('Welcome Email Sent', 'Shows whether the welcome email has been sent.'),
            item('View Scheduling Preferences Summary', 'Opens a summary of this user’s scheduling preferences.'),
            item('Copy iCal URL', 'Copies the calendar subscription link when one is available.'),
            item('Save', 'Save changes to the user profile.'),
        ])],
    },
    'user.availability': {
        title: 'User availability',
        intro: 'Availability records tell the scheduler when this user can or cannot serve.',
        sections: [section('Values in the list', [
            item('Name and Description', 'Identify the availability record and its notes.'),
            item('Recurrence', 'Shows the repeating date pattern and its date limits.'),
            item('Available', 'Indicates whether the recurrence marks available or unavailable time.'),
            item('Start Date and End Date', 'Show the date range covered by the availability record.'),
            item('Last Edit Date', 'Shows when the record was last changed.'),
            item('Add New Availability, Edit, and Delete', 'Create or maintain the user’s availability records.'),
        ])],
    },
    'user.preferred-activities': {
        title: 'User preferences',
        intro: 'Manage the activities and activity workgroups this user prefers for scheduling.',
        sections: [
            section('Preferred activities', [
                item('All Activities', 'Activities available to add to the user’s preferences.'),
                item("User's Preferred Activities", 'Activities currently selected as preferences for this user.'),
                item('Name and Description', 'Identify each activity and describe it.'),
                item('Save', 'Apply changes to activity preferences.'),
            ]),
            section('Preferred activity workgroups', [
                item('Preferred Activities Workgroups', 'Workgroups available to add to the user’s preferences.'),
                item("User's Preferred Activity Workgroups (user schedules will ONLY be generated for selected workgroups)", 'Workgroups currently selected for this user.'),
                item('Name and Description', 'Identify each workgroup and describe its role.'),
                item('Save', 'Apply changes to workgroup preferences.'),
            ]),
        ],
    },
    'user.schedule-preferences': {
        title: 'User scheduling rules',
        intro: 'These rules shape how the scheduler evaluates this user. A rule can exclude the user when its condition is met or act as a preference when possible.',
        sections: [
            section('Existing rules', [
                item('ID, Name, and Description', 'Identify each scheduling rule and summarize what it checks.'),
                item('Delete', 'Remove a saved rule when the delete control is available.'),
            ]),
            section('Rule settings', [
                item('Action: Never', 'Treats the rule as a hard constraint and excludes the user when its condition is met.'),
                item('Action: When Possible', 'Treats the rule as a soft preference that adjusts scheduling probability without excluding the user.'),
                item('Rule fields', 'Set the values or people relevant to the selected rule; available fields vary by rule.'),
                item('Save rule and Learn more', 'Save an individual rule or open its information panel.'),
            ]),
        ],
    },
    'user.assigned-skills': {
        title: 'Assigned skills',
        intro: 'Review the skills assigned to this user and manage their skill levels.',
        sections: [section('Values in the list', [
            item('Skill Name and Description', 'Identify each assigned skill and describe it.'),
            item('Skill Workgroups', 'Show activities and workgroups that use the skill.'),
            item('Skill Level', 'The user’s proficiency value; the editor notes that 10 is expert level.'),
            item("Manage User's skills", 'Open the controls for assigning or removing skills.'),
            item('Save and Edit', 'Save a skill-level value or open the skill-level editor.'),
        ])],
    },
    'user.admin-groups': {
        title: 'Admin groups',
        intro: 'Manage the administrative roles and activity or workgroup access assigned to this user. Available controls depend on your permissions.',
        sections: [
            section('Organization and location roles', [
                item('Organization Admin', 'Grants administrative access for the displayed organization when permitted.'),
                item('Location Admin', 'Grants administrative access for the displayed location; organization access may provide inherited location access.'),
                item('Save Admin Permissions', 'Save changes to organization or location admin roles.'),
            ]),
            section('Activity and workgroup access', [
                item('Activities and Events List', 'Activities available for assignment as admin activities.'),
                item('Admin activities and events', 'Activities for which this user currently has admin access.'),
                item('Workgroups List', 'Workgroups available for assignment as admin workgroups.'),
                item('Admin Workgroups', 'Workgroups for which this user currently has admin access.'),
                item('Save', 'Apply changes to the activity or workgroup admin lists.'),
            ]),
        ],
    },
    'user.available-workgroups': {
        title: 'Available workgroup teams',
        intro: 'Choose which workgroup teams are associated with this user.',
        sections: [section('Values in the lists', [
            item("Available Workgroups' Teams", 'Teams available to associate with this user.'),
            item("User's workgroups' teams", 'Teams currently associated with this user.'),
            item('Team Name', 'Identifies the workgroup team.'),
            item('Members', 'Shows the number of people in the team.'),
            item('Save', 'Apply changes to the user’s team selections.'),
        ])],
    },
    'user.notifications': {
        title: 'Notification preferences',
        intro: 'Choose which schedule and sign-up email or text notifications this user receives, and set the timing for consolidated emails.',
        sections: [section('Notification rules', [
            item('Assignment summary email', 'Set how often a consolidated email is sent and how far ahead it lists assignments.'),
            item('Assignment group email', 'Choose whether to email before an assigned event when other people are serving with the user.'),
            item('Assignment text reminder', 'Choose whether to text the user before an event where they have an assignment.'),
            item('Sign-up opportunity email', 'Set how often a consolidated email is sent and how far ahead it lists eligible open sign-up opportunities.'),
            item('Send and Do NOT Send', 'Choose whether an individual notification rule is enabled.'),
            item('Save rule and Learn more', 'Save an individual rule or open its information panel.'),
        ])],
    },
    'user.interested-skills': {
        title: 'Interested skills',
        intro: 'Review skills the user is interested in and assign an available skill when appropriate.',
        sections: [section('Values in the list', [
            item('Skill Name and Description', 'Identify each skill and describe it.'),
            item('Assign skill', 'Use the row action to assign that skill to the user.'),
            item('Update Interest Skills', 'Open the volunteer sign-up page to update the user’s skill interests.'),
        ])],
    },
    'user.user-assignments': {
        title: 'User assignments',
        intro: 'Review the user’s declined assignments and access their next assignment schedule.',
        sections: [section('Values in the list', [
            item('ID', 'The assignment record identifier.'),
            item('Workgroup Name', 'The workgroup for the assignment.'),
            item('Activity Name → Date', 'The activity and its scheduled date.'),
            item('Decline Date', 'When the assignment was declined, when available.'),
            item('Response Method', 'How the user responded to the assignment.'),
            item('Status', 'The assignment status; this list currently displays declined assignments.'),
            item('Restore assignment', 'Use the row action to undo the decline when available.'),
            item("View User's next assignment(s)", 'Open the user’s next assignment schedule.'),
        ])],
    },
};